import React, { useState, useRef, useEffect } from 'react';
import theme from '../theme';

const activePlayers = new Set();

function AudioPlayer({ url, isMe }) {
  const audioRef = useRef(null);
  const instanceRef = useRef({});
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);

  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
      }
      activePlayers.delete(instanceRef.current);
    };
  }, []);

  const stopThis = () => {
    if (audioRef.current) audioRef.current.pause();
    setPlaying(false);
  };

  const togglePlay = () => {
    if (!audioRef.current) return;

    if (playing) {
      audioRef.current.pause();
      setPlaying(false);
    } else {
      activePlayers.forEach(player => {
        if (player !== instanceRef.current && player.stop) {
          player.stop();
        }
      });
      activePlayers.add(instanceRef.current);
      instanceRef.current.stop = stopThis;

      audioRef.current.play();
      setPlaying(true);
    }
  };

  const formatSec = (s) => {
    if (!s || isNaN(s) || !isFinite(s)) return '0:00';
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60);
    return `${m}:${sec.toString().padStart(2, '0')}`;
  };

  const handleLoadedMetadata = () => {
    if (audioRef.current && isFinite(audioRef.current.duration)) {
      setDuration(audioRef.current.duration);
    }
  };

  const handleCanPlay = () => {
    if (audioRef.current && isFinite(audioRef.current.duration) && duration === 0) {
      setDuration(audioRef.current.duration);
    }
  };

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: theme.spacing.sm, minWidth: 180 }}>
      <audio
        ref={audioRef}
        src={url}
        preload="metadata"
        onTimeUpdate={() => {
          if (audioRef.current) setProgress(audioRef.current.currentTime);
        }}
        onLoadedMetadata={handleLoadedMetadata}
        onCanPlay={handleCanPlay}
        onEnded={() => {
          setPlaying(false);
          setProgress(0);
          activePlayers.delete(instanceRef.current);
        }}
      />

      <button
        onClick={togglePlay}
        style={{
          width: 36, height: 36, borderRadius: theme.radius.circle,
          background: isMe ? 'rgba(255,255,255,0.3)' : theme.colors.primary,
          border: 'none', color: theme.colors.white, fontSize: theme.font.size.md,
          cursor: 'pointer', display: 'flex', alignItems: 'center',
          justifyContent: 'center', flexShrink: 0,
        }}>
        {playing ? '⏸' : '▶'}
      </button>

      <div style={{ flex: 1 }}>
        <input
          type="range"
          min={0}
          max={duration || 1}
          value={progress}
          onChange={e => {
            if (audioRef.current) {
              audioRef.current.currentTime = parseFloat(e.target.value);
              setProgress(parseFloat(e.target.value));
            }
          }}
          style={{ width: '100%', accentColor: isMe ? theme.colors.white : theme.colors.primary, cursor: 'pointer' }}
        />
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: theme.font.size.xs, color: isMe ? 'rgba(255,255,255,0.7)' : theme.colors.textMuted }}>
          <span>{formatSec(progress)}</span>
          <span>{formatSec(duration)}</span>
        </div>
      </div>
    </div>
  );
}

export default AudioPlayer;